import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Country } from '../data/countries';

export interface CountryFormDialogProps {
  open: boolean;
  country: Country | null;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export default function CountryFormDialog({
  open,
  country,
  onClose,
  onSave,
}: CountryFormDialogProps) {
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setName(country?.name || '');
      setError('');
    }
  }, [open, country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError(t('countries.nameRequired'));
      return;
    }

    if (!/^[a-zA-Z\u0600-\u06FF]/.test(name.trim())) {
      setError(t('countries.nameMustStartWithLetter'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSave(name.trim());
      onClose();
    } catch (err) {
      setError((err as Error).message || t('messages.errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {country ? t('countries.editCountry') : t('countries.createCountry')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('countries.countryName')}
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={isSubmitting}
            sx={{ mt: 2 }}
            InputLabelProps={{
              sx: {
                transformOrigin: isRtl ? 'top right' : 'top left',
                right: isRtl ? 0 : 'auto',
                left: isRtl ? 'auto' : 0,
              },
            }}
            FormHelperTextProps={{
              sx: {
                textAlign: isRtl ? 'right' : 'left',
                marginRight: isRtl ? '14px' : 0,
                marginLeft: isRtl ? 0 : '14px',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: '#028bcb !important',
              '&:hover': { background: '#0277a8 !important' },
            }}
          >
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

